import { IFetchQuery, IUnknownRecord } from "@cd2/skeem-client"
import { useEffect, useState } from "react"
import { NetworkStatus } from "./useFetchOld"

type IQuery = IFetchQuery

interface IuseFetchOneOptions {
  // if true then the query will not be executed
  skip?: boolean
  // variables to pass to the query function
  variables?: Record<string, any>
}

type IuseFetchOneReturn<T extends {} = IUnknownRecord> = {
  query: IQuery
  variables?: Record<string, any>
  networkStatus: NetworkStatus
  refetch(variables?: any): void
} & (
  | {
      data?: T
      isLoading: true
      error: Record<string, any>
    }
  | {
      data: T
      isLoading: false
      error: undefined
    }
  | {
      data?: T
      isLoading: false
      error: Record<string, any>
    }
)

export function useFetchOne<T>(
  query: (variables: Record<string, any>) => IFetchQuery,
  options?: IuseFetchOneOptions,
): IuseFetchOneReturn<T & { id: string }>
export function useFetchOne<T>(
  query: IFetchQuery,
  options?: Omit<IuseFetchOneOptions, "variables">,
): IuseFetchOneReturn<T & { id: string }>
export function useFetchOne(
  query: IFetchQuery | ((variables: Record<string, any>) => IFetchQuery),
  options: IuseFetchOneOptions = {},
): any {
  if (options.variables && typeof query !== "function") {
    throw new Error("Cannot use the variables option unless the query given is a function")
  }

  const skeem = window.skeem
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>(NetworkStatus.loading)

  const [results, setResults] = useState({
    data: undefined as any,
    error: undefined as any,
    query: undefined as any,
    variables: {} as any,
  })

  useEffect(() => {
    if (options.skip) {
      return
    }
    setNetworkStatus(NetworkStatus.loading)
    runQuery(options.variables)
  }, [])

  async function runQuery(variables: any) {
    const finalQuery: any = typeof query === "function" ? query(variables ?? {}) : query
    try {
      const data = await skeem.fetchOne(finalQuery)
      setResults({
        data,
        error: undefined,
        query: finalQuery,
        variables,
      })
      setNetworkStatus(NetworkStatus.ready)
    } catch (error) {
      setResults({
        data: undefined,
        error,
        query: finalQuery,
        variables,
      })
      setNetworkStatus(NetworkStatus.error)
    }
  }

  function refetch(variables?: any) {
    if (variables && typeof query !== "function") {
      throw new Error("Cannot use the variables option unless the query given is a function")
    }
    setNetworkStatus(NetworkStatus.refetch)
    return runQuery(variables ?? options.variables)
  }

  const isLoading =
    networkStatus === NetworkStatus.loading || networkStatus === NetworkStatus.refetch

  return {
    refetch,
    networkStatus,
    isLoading,
    ...results,
  }
}
